/**
 * Provider-aware subscription management (S5).
 *
 * Where a subscriber manages their plan depends on where they bought it — not on
 * the device they're using. An Apple subscriber on the web still manages through
 * Apple. These pure helpers map the backend's `subscription_source` to the right
 * action so the UI never assumes Stripe.
 */
export type SubscriptionSource =
  | 'stripe'
  | 'apple_iap'
  | 'google_play'
  | null
  | undefined;

export type ManageMode = 'stripe_portal' | 'app_store' | 'support';

const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const GOOGLE_PLAY_SUBSCRIPTIONS_URL =
  'https://play.google.com/store/account/subscriptions?package=com.arnkimdev.vizday';

export function manageMode(source: SubscriptionSource): ManageMode {
  if (source === 'stripe') return 'stripe_portal';
  if (source === 'apple_iap' || source === 'google_play') return 'app_store';
  // No provider source but still entitled → founding/manual Plus: support only.
  return 'support';
}

/** External store URL for app-store subscriptions, or null when management is
 *  in-app (Stripe portal) or unavailable (manual/founding). */
export function externalManagementUrl(source: SubscriptionSource): string | null {
  if (source === 'apple_iap') return APPLE_SUBSCRIPTIONS_URL;
  if (source === 'google_play') return GOOGLE_PLAY_SUBSCRIPTIONS_URL;
  return null;
}

export function manageLabel(source: SubscriptionSource): string {
  switch (manageMode(source)) {
    case 'stripe_portal':
      return 'Manage billing';
    case 'app_store':
      return source === 'apple_iap'
        ? 'Managed through Apple'
        : 'Managed through Google Play';
    case 'support':
      return 'Contact support to manage';
  }
}
